'use client'
import { Checkbox, type CheckboxProps, Text } from '@chakra-ui/react'
import type { Control, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form'
import {
  FormDescription,
  FormField,
  FormFieldControl,
  FormFieldLabel,
  FormMessage
} from '../FormField'

interface GrandFormCheckboxFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> extends Omit<CheckboxProps, 'name' | 'isChecked' | 'onChange' | 'value' | 'defaultChecked'> {
  name: TName
  label?: string
  description?: string
  checkboxText?: string
  required?: boolean
  optional?: boolean
  disabled?: boolean
  rules?: RegisterOptions<TFieldValues, TName>
  control?: Control<TFieldValues>
}

export const GrandFormCheckboxField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
>({
  name,
  label,
  description,
  checkboxText,
  required,
  optional,
  disabled,
  rules,
  control,
  ...checkboxProps
}: GrandFormCheckboxFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      optional={optional}
      required={required}
      rules={rules}
      render={({ field: { value, onChange, onBlur, ref } }) => (
        <FormFieldControl disabled={disabled}>
          {label ? <FormFieldLabel>{label}</FormFieldLabel> : null}

          <Checkbox
            isChecked={!!value}
            isDisabled={disabled}
            onBlur={onBlur}
            onChange={(e) => onChange(e.target.checked)}
            ref={ref}
            {...checkboxProps}
          >
            {checkboxText ? (
              <Text fontSize="sm" fontWeight="medium">
                {checkboxText}
              </Text>
            ) : null}
          </Checkbox>

          {description ? <FormDescription>{description}</FormDescription> : null}

          <FormMessage />
        </FormFieldControl>
      )}
    />
  )
}
